/** Acceso movil estilo tarjeta centrada (design.md §22.2 y §22.7). */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema, type LoginForm } from '@/schemas/login.schemas';
import { authService } from '@/services/authService';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const passwordChanged = searchParams.get('passwordChanged') === 'true';
  const { refresh } = useMobileAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: yupResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const emailValue = watch('email') ?? '';
  const passwordValue = watch('password') ?? '';
  const emailValid = emailValue.trim().length > 0 && !errors.email;
  const passwordValid = passwordValue.length > 0 && !errors.password;

  async function onSubmit(values: LoginForm) {
    setGeneralError(null);
    try {
      const user = await authService.login(values.email.trim().toLowerCase(), values.password);
      await refresh();
      try {
        const memberships = await authService.memberships();
        navigate(memberships.length === 1 ? '/m' : '/m/company', { replace: true });
      } catch {
        navigate(user.active_tenant_id ? '/m' : '/m/company', { replace: true });
      }
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    }
  }

  async function recover() {
    const email = getValues('email').trim().toLowerCase();
    if (email === '') {
      setGeneralError('Escribe tu correo para recuperar la clave');
      return;
    }
    if (recoveryBusy) return;
    setGeneralError(null);
    setRecoveryBusy(true);
    try {
      await authService.forgotPassword(email);
      setRecoverySent(true);
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    } finally {
      setRecoveryBusy(false);
    }
  }

  function toggleRecovering() {
    setRecovering((v) => !v);
    setRecoverySent(false);
    setGeneralError(null);
  }

  const title = recovering ? 'Recupera tu acceso' : '¡Bienvenido de nuevo!';
  const subtitle = recovering
    ? 'Escribe tu correo para enviarte instrucciones.'
    : 'Ingresa con tu correo y contraseña.';

  return (
    <div className="flex min-h-dvh flex-col bg-app safe-area-top safe-area-bottom">
      {/* Marca centrada estilo referencia de acceso */}
      <div className="flex flex-col items-center px-6 pb-8 pt-16">
        <img
          src="/rpa-value-logo.png"
          alt="RPA Value"
          className="mb-6 h-auto w-[160px]"
        />
        <h1 className="text-center text-2xl font-bold text-text-heading">{title}</h1>
        <p className="mt-1 text-center text-sm text-text-secondary">{subtitle}</p>
      </div>

      {/* Tarjeta blanca flotante con el formulario */}
      <div className="flex-1 px-5 pb-8">
        <div className="rounded-card bg-white p-6 shadow-card">
          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
            <MobileInput
              label="Correo"
              type="email"
              autoComplete="username"
              enterKeyHint="next"
              placeholder="tu@correo.com"
              staticLabel
              isRequired
              icon={<MobileIcon name="mail" className="h-5 w-5" />}
              iconRight={
                emailValid ? (
                  <MobileIcon name="check_circle" className="h-5 w-5 text-success" />
                ) : undefined
              }
              error={errors.email?.message}
              {...register('email')}
            />

            {!recovering && (
              <MobileInput
                label="Clave"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                enterKeyHint="go"
                placeholder="••••••••"
                staticLabel
                isRequired
                icon={<MobileIcon name="lock" className="h-5 w-5" />}
                iconRight={
                  <span className="flex items-center gap-1">
                    {passwordValid && (
                      <MobileIcon name="check_circle" className="h-5 w-5 text-success" />
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-bg-tertiary"
                      aria-label={showPassword ? 'Ocultar clave' : 'Mostrar clave'}
                      aria-pressed={showPassword}
                    >
                      <MobileIcon
                        name={showPassword ? 'visibility_off' : 'visibility'}
                        className="h-5 w-5 text-text-secondary"
                      />
                    </button>
                  </span>
                }
                error={errors.password?.message}
                {...register('password')}
              />
            )}

            {generalError && (
              <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
                <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
                <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
              </div>
            )}

            {passwordChanged && (
              <div className="flex items-center gap-2 rounded-2xl bg-success-light px-4 py-3">
                <MobileIcon name="check_circle" className="h-5 w-5 shrink-0 text-success" />
                <p className="min-w-0 flex-1 break-words text-sm text-success-dark">
                  Contraseña actualizada. Ingresa con tu nueva contraseña.
                </p>
              </div>
            )}

            {recoverySent && (
              <div className="flex items-center gap-2 rounded-2xl bg-success-light px-4 py-3">
                <MobileIcon name="check_circle" className="h-5 w-5 shrink-0 text-success" />
                <p className="min-w-0 flex-1 break-words text-sm text-success-dark">
                  Si el correo existe, recibirás las instrucciones.
                </p>
              </div>
            )}

            {!recovering ? (
              <>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={toggleRecovering}
                    className="flex min-h-[44px] items-center text-sm font-medium text-primary hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <MobileButton
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  className="min-h-[52px]"
                >
                  Ingresar
                </MobileButton>
              </>
            ) : (
              <>
                <MobileButton
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={recoveryBusy}
                  onClick={() => void recover()}
                  className="min-h-[52px]"
                  icon={<MobileIcon name="send" className="h-5 w-5" />}
                >
                  Enviar instrucciones
                </MobileButton>
                <button
                  type="button"
                  onClick={toggleRecovering}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 text-sm font-medium text-primary"
                >
                  <MobileIcon name="arrow_back" className="h-5 w-5" />
                  Volver al ingreso
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
